import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowUpRightFromSquare, faKey } from '@fortawesome/free-solid-svg-icons';

import api from '../../api';
import Waitlist from '../Waitlist';


/**
 * How much of the run has sold.
 *
 * Returns null until it resolves and false when it can't. Both render the
 * static run line instead of the counter, so a Stripe outage costs us the
 * counter and nothing else -- the buy button below is a plain link and does
 * not go through our API at all.
 */
const useStock = () => {
  const [stock, setStock] = useState(null);

  useEffect(() => {
    let cancelled = false;

    api.merch.getStock()
      .then((data) => { if (!cancelled) setStock(data); })
      .catch(() => { if (!cancelled) setStock(false); });

    return () => { cancelled = true; };
  }, []);

  return stock;
};


// The artwork is a transparent die-cut, so it floats on the stage rather than
// sitting on a card -- the silhouette IS the product and a card would hide it.
const StickerStage = ({ image, alt }) => {
  return (
    <div className='group relative grid aspect-square place-items-center overflow-hidden rounded-lg border border-commit-purple bg-drop-stage'>
      <img
        src={image}
        alt={alt}
        className='w-[72%] -rotate-2 drop-shadow-sticker transition-transform duration-500 group-hover:-translate-y-1.5 group-hover:-rotate-1'
      />
      <span className='pointer-events-none absolute inset-0 bg-drop-vignette' />
    </div>
  );
};


const Eyebrow = ({ children }) => {
  return (
    <span className='inline-flex items-center gap-2.5 self-start rounded-full border border-commit-gold/40 bg-commit-purple/60 px-3 py-1.5 font-mono text-[11px] tracking-[0.19em] text-commit-gold'>
      <span className='h-1.5 w-1.5 rounded-full bg-commit-gold' />
      {children}
    </span>
  );
};


// The product is named after a commit, so its spec sheet is one.
const CommitBlock = ({ hash, refs, author, date, message }) => {
  return (
    <pre className='overflow-x-auto rounded-lg border-y border-r border-l-2 border-neutral-800 border-l-commit-gold bg-neutral-950 p-4 font-mono text-xs leading-loose text-neutral-400'>
      <span className='text-neutral-500'>commit</span> <span className='text-commit-gold'>{hash}</span> <span className='text-primary-light'>({refs})</span>
      {'\n'}<span className='text-neutral-500'>Author:</span> {author}
      {'\n'}<span className='text-neutral-500'>Date:</span>   {date}
      {'\n\n'}<span className='text-neutral-50'>    {message}</span>
    </pre>
  );
};


const RunCounter = ({ total, sold, remaining }) => {
  const claimed = Math.min(100, Math.round((sold / total) * 100));

  return (
    <div className='flex flex-col gap-2'>
      <div className='h-[3px] overflow-hidden rounded-sm bg-neutral-800'>
        <span
          className='block h-full bg-commit-gold transition-[width] duration-700 ease-out'
          style={{ width: `${claimed}%` }}
        />
      </div>
      <p className='font-mono text-[11.5px] tracking-wide text-neutral-500'>
        <span className='text-commit-gold'>{sold}</span> claimed
        {' · '}
        <span className='text-commit-gold'>{remaining}</span> left of {total}
      </p>
    </div>
  );
};


const StaticRun = ({ total }) => {
  return (
    <p className='flex items-center gap-3 font-mono text-[11.5px] tracking-[0.1em] text-commit-gold'>
      LIMITED RUN OF {total}
      <span className='h-px flex-1 bg-gradient-to-r from-commit-gold/40 to-transparent' />
    </p>
  );
};


const Spec = ({ items }) => {
  return (
    <section className='border-y border-neutral-800 py-11'>
      <h2 className='mb-6 font-mono text-xs tracking-[0.19em] text-neutral-500'>SPECIFICATION</h2>
      <dl className='grid grid-cols-2 gap-x-8 gap-y-6 sm:grid-cols-4'>
        {items.map(({ label, value }) => (
          <div key={label}>
            <dt className='mb-1 font-mono text-[11px] tracking-[0.14em] text-neutral-500'>{label}</dt>
            <dd className='font-body text-base text-neutral-50'>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};


const Drop = ({
  eyebrow,
  title,
  kicker,
  perk,
  price,
  runSize,
  image,
  imageAlt,
  commit,
  spec,
  checkoutUrl,
  nextDropHeading,
  nextDropBlurb
}) => {
  const stock = useStock();

  const soldOut = Boolean(stock) && (stock.remaining === 0 || !stock.availableForSale);

  return (
    <div className='mx-auto max-w-6xl px-4 sm:px-6 lg:px-8'>

      <section className='grid grid-cols-1 items-center gap-12 py-12 md:grid-cols-2 md:gap-16 md:py-20'>
        <StickerStage image={image} alt={imageAlt} />

        <div className='flex flex-col gap-6'>
          <Eyebrow>{eyebrow}</Eyebrow>

          <h1 className='font-heading text-5xl leading-none tracking-tight text-neutral-50 sm:text-6xl'>{title}</h1>

          <p className='max-w-[46ch] font-body text-lg leading-relaxed text-neutral-400'>{kicker}</p>

          <CommitBlock {...commit} />

          {/* Sits under the commit block, which separates what the sticker is
              from what holding one gets you. Runs the full width of the column
              rather than the kicker's 46ch measure -- at the narrower measure
              it wrapped into four short lines behind a leading icon, which
              reads as a bullet rather than a note. */}
          <p className='flex items-start gap-2.5 font-body text-[15px] leading-relaxed text-commit-cream'>
            <FontAwesomeIcon icon={faKey} className='mt-1.5 flex-shrink-0 text-commit-gold' size='xs' />
            {perk}
          </p>

          {stock
            ? <RunCounter total={stock.total} sold={stock.sold} remaining={stock.remaining} />
            : <StaticRun total={runSize} />}

          <span className='font-mono text-2xl text-neutral-50'>{price}</span>

          {soldOut ? (
            <div className='flex h-11 items-center justify-center rounded-lg border border-neutral-800 bg-neutral-900 px-6 font-body text-[15px] tracking-wide text-neutral-500'>
              Sold out
            </div>
          ) : (
            <a
              href={checkoutUrl}
              className={classNames(
                'flex h-11 items-center justify-center gap-2.5 rounded-lg bg-primary px-6 font-body text-[15px] tracking-wide text-white shadow-sm',
                'transition-colors duration-150 hover:bg-primary-dark',
                'focus:outline-none focus:ring-2 focus:ring-primary-light focus:ring-offset-2 focus:ring-offset-neutral-950'
              )}
            >
              Buy this sticker
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} size='sm' className='opacity-80' />
            </a>
          )}

          <p className='flex items-start gap-2.5 font-body text-sm text-neutral-500'>
            <FontAwesomeIcon icon={faArrowRight} className='mt-1.5 flex-shrink-0 text-primary' size='xs' />
            Checkout is handled by Stripe; no account needed. One sticker per order; check out again for a second.
          </p>
        </div>
      </section>

      <Spec items={spec} />

      <section className='py-16'>
        <h2 className='mb-2 font-heading text-3xl text-neutral-50'>{nextDropHeading}</h2>
        <p className='mb-8 max-w-[52ch] font-body text-neutral-400'>{nextDropBlurb}</p>
        <Waitlist />
      </section>

    </div>
  );
};

export default Drop;
